import { useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import CategoryHero from "./components/CategoryHero";
import CategoryGrid from "./components/CategoryGrid";
import { categories, comics } from "@/mocks/comics";
import NotFound from "@/pages/NotFound";

const categoryNames: Record<string, string> = {
  wuxia: "武侠",
  fantasy: "奇幻",
  romance: "恋爱",
  suspense: "悬疑",
  historical: "古风",
  school: "校园",
};

const categoryCanonicalUrls: Record<string, string> = {
  wuxia: "http://hongtongren.com/category/wuxia",
  fantasy: "http://hongtongren.com/category/fantasy",
  romance: "http://hongtongren.com/category/romance",
  suspense: "http://hongtongren.com/category/suspense",
  historical: "http://hongtongren.com/category/historical",
  school: "http://hongtongren.com/category/school",
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const categoryName = slug ? categoryNames[slug] : null;
  const categoryData = categories.find((c) => c.slug === slug);

  if (!categoryName || !categoryData) {
    return <NotFound />;
  }

  const filteredComics = comics.filter((c) => c.category === categoryName);

  const pageSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${categoryName}漫画 - 韩漫网`,
    description: `韩漫网${categoryName}漫画分类，为您提供高清${categoryName}韩国漫画免费在线阅读。`,
    url: categoryCanonicalUrls[slug],
    isPartOf: {
      "@type": "WebSite",
      name: "韩漫网",
      url: "http://hongtongren.com/",
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(pageSchema) }}
      />
      <Navbar />
      <main>
        <div className="pt-16">
          <Breadcrumb
            items={[
              { name: "首页", href: "/" },
              { name: `${categoryName}漫画` },
            ]}
          />
        </div>
        <CategoryHero
          name={categoryName}
          slug={slug}
          icon={categoryData.icon}
          description={categoryData.description}
        />

        <section className="w-full py-10 md:py-14 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-8 md:gap-12">
              <div className="lg:w-2/3">
                <h3 className="text-lg font-bold text-foreground-900 mb-4">
                  关于{categoryName}韩漫
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                  {categoryName}是韩漫中最受欢迎的类型之一。韩漫网为您精选了大量高清{categoryName}韩国漫画，
                  涵盖热门连载和经典完结作品，全部免费在线阅读。无论您是{categoryName}韩漫的忠实粉丝，
                  还是刚刚接触韩漫的新读者，都能在这里找到心仪的作品。
                </p>
                <div className="flex items-center gap-8 md:gap-12">
                  <div>
                    <p className="text-2xl md:text-3xl font-black text-foreground-950">
                      {filteredComics.length}
                    </p>
                    <p className="text-xs text-foreground-400 mt-1">部作品</p>
                  </div>
                  <div>
                    <p className="text-2xl md:text-3xl font-black text-foreground-950">9.5+</p>
                    <p className="text-xs text-foreground-400 mt-1">平均评分</p>
                  </div>
                </div>
              </div>

              <div className="lg:w-1/3">
                <div className="relative overflow-hidden rounded-lg aspect-[3/4] max-w-[240px] mx-auto lg:mx-0">
                  <img
                    src={filteredComics[0]?.cover || comics[0].cover}
                    alt={`${categoryName}漫画代表作 - 韩漫网`}
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute top-3 left-3">
                    <span className="bg-white text-foreground-900 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">
                      代表作
                    </span>
                  </div>
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                    <p className="text-white text-sm font-semibold whitespace-nowrap">
                      {filteredComics[0]?.title || "热门作品"}
                    </p>
                    <p className="text-white/70 text-xs whitespace-nowrap">
                      {filteredComics[0]?.latestChapter || ""}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <CategoryGrid category={categoryName} />
      </main>
      <Footer />
    </>
  );
}